import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  CellDateV8,
  toastSuccess,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { getRouteByName } from 'app/routes';
import { formatFloat } from 'shared-pages/utils/helpers';
import { Button } from 'react-bootstrap';
import i18n from 'i18n';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { gqlUpdate } from './graphql';
import { useMutation } from '@apollo/client';
import { useHandleError } from 'shared-pages/services';

export const GridPage = forwardRef((props: any, ref) => {
  const {
    data: inData,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    initOrderBy,
    onOrderChange,
    refetch,
    onClose,
    collectionOrderId,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t } = useTranslation();
  const { handleGraphqlError } = useHandleError('routes');
  const [data, setData] = useState(inData);
  const [selected, setSelected] = useState<any>(null);
  const [updateRouteMutation] = useMutation(gqlUpdate);

  const columns = useMemo(
    () => [
      {
        id: 'code',
        header: t('Κωδικός Δρομολογίου'),
        accessorKey: 'code',
        orderByField: 'code',
        cell: TableCellV8,
        size: 20,
      },
      {
        id: 'routeDate',
        header: t('Ημερομηνία Δρομολογίου'),
        accessorKey: 'routeDate',
        orderByField: 'routeDate',
        cell: CellDateV8,
        size: 20,
      },
      {
        id: 'collector',
        header: t('Συλλέκτης'),
        accessorKey: 'vehicle.collector.name',
        orderByField: 'vehicle.collector.name',
        cell: TableCellV8,
        size: 50,
      },
      {
        id: 'vehicle',
        header: t('Κωδικός Οχήματος'),
        accessorKey: 'vehicle.plateNumber',
        orderByField: 'vehicle.plateNumber',
        cell: TableCellV8,
        size: 50,
      },
      {
        id: 'driver',
        header: t('Οδηγός'),
        accessorKey: 'driver.name',
        orderByField: 'driver.name',
        cell: TableCellV8,
        size: 50,
      },
    ],
    [t],
  );

  const onTableRowClick = (table) => {
    // select current row only
    table.toggleAllRowsSelected(false);
    table.row.toggleSelected(true);
    // store selection
    setSelected(table.row.original);
  };
  const tableRef = useRef(null);
  useImperativeHandle(ref, () => tableRef.current);

  //logic to update the Route needs validation
  const handleRoute = useCallback(async () => {
    if (!selected) return;

    // Check if collectionOrderId is already in the selected route
    const alreadyExists = selected.routesLines?.some(
      (line) => line.collectionOrderId === collectionOrderId,
    );

    if (alreadyExists) {
      handleGraphqlError('already,exists', 'Η εντολή υπάρχει ήδη σε αυτό το Δρομολόγιο');
      return;
    }

    // Safely determine the next `aa` value (in case it's not sorted or undefined)
    const maxAa = selected.routesLines?.length
      ? Math.max(...selected.routesLines.map((l) => l.aa || 0))
      : 0;

    const nextAa = maxAa + 1;
    await updateRouteMutation({
      variables: {
        data: {
          id: selected.id,

          routesLines: [
            {
              aa: selected.routesLines.length + 1,
              routeId: selected.id,
              recordDate: new Date(),
              collectionOrderId,
              statusId: 1,
              isActive: true,
            },
          ],
        },
      },
    })
      .then(() => {
        toastSuccess(`Η εντολή προστέθηκε στο Δρομολόγιο [${selected.id}]`);

        onClose(true);
        history.go(0);
      })
      .catch((error) => {
        handleGraphqlError('mutation-update', error);
      });
  }, [selected, collectionOrderId, updateRouteMutation, refetch, onClose]);

  return (
    <div>
      <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
        {data ? (
          <TablePaginatedV8
            ref={tableRef}
            loading={loading}
            id="routes-grid"
            appStore={appStore}
            tableStore={tableStore}
            columns={columns as any}
            data={data}
            dataCount={dataCount}
            topBorderRadius={false}
            onRowClick={onTableRowClick}
            selectedRowIndex={selected ? 0 : null}
            fixedPageSize={10}
            style={{
              minWidth: 'inherit !important',
              minHeight: '340px',
              overflowY: 'scroll',
            }}
            rowProps={(row) => (row.getIsSelected() ? { className: 'table-warning' } : null)}
            onPageChange={onPageChange}
            initPageIndex={initPageIndex}
            initSorting={order2Sorting(initOrderBy)}
            onSortByChange={(sort) => {
              const order = `${sort[0].id} ${sort[0].desc ? 'DESC' : 'ASC'}`;
              onOrderChange(order);
            }}
          />
        ) : (
          <div style={{ height: '200px' }}></div>
        )}
      </LoadingOverlay>
      <div className="float-right mt-4 pt-3 mb-0">
        <Button
          className="me-3"
          variant="primary"
          size="lg"
          style={{ paddingTop: '4px', paddingBottom: '4px', minWidth: '7rem' }}
          onClick={handleRoute}
          disabled={!selected}
          id="okButton"
        >
          <FaCheck /> &nbsp;
          {i18n.language === 'gr' ? t('OK') : t('OK')}
        </Button>
        <Button
          variant="danger"
          size="lg"
          style={{ paddingTop: '4px', paddingBottom: '4px' }}
          onClick={() => onClose(false)}
        >
          <FaTimes /> &nbsp;
          {i18n.language === 'gr' ? t('Άκυρο') : t('Cancel')}
        </Button>
      </div>
    </div>
  );
});
